import React from 'react';
import axios from 'axios';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Checkbox } from '../../ui/checkbox';
import { EMPTY_SA_FORM } from './salesAreaDefaults';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Add / Edit dialog for a customer Sales Area assignment.
 */
const SalesAreaDialog = ({ ctx }) => {
  const {
    saFormData, setSaFormData,
    editingSaIndex, setEditingSaIndex, setShowSaDialog,
    salesOrgs, distChannels, filteredDistChannels, setFilteredDistChannels,
    divisions, filteredDivisions, setFilteredDivisions,
    salesDistricts, customerGroups,
    salesOffices, salesGroups, filteredSalesGroups, setFilteredSalesGroups,
    currencies, deliveryPriorities, shippingConditions, plants,
    incoterms, creditControlAreas, salesAreas, termsOfPayment,
    handleAddSalesArea,
  } = ctx;

  const set = (patch) => setSaFormData({ ...saFormData, ...patch });

  const onSalesOrgChange = (value) => {
    setSaFormData({ ...saFormData, sales_org_id: value, dist_channel_id: '', division_id: '' });
    const validSalesAreasForSO = salesAreas.filter((sa) => sa.sales_org_id === value);
    const validDCIds = [...new Set(validSalesAreasForSO.map((sa) => sa.dist_channel_id))];
    setFilteredDistChannels(distChannels.filter((dc) => validDCIds.includes(dc.dist_channel_id)));
    const validDivIds = [...new Set(validSalesAreasForSO.map((sa) => sa.division_id))];
    setFilteredDivisions(divisions.filter((div) => validDivIds.includes(div.division_id)));
  };

  const onSalesOfficeChange = async (value) => {
    setSaFormData({ ...saFormData, sales_office_id: value, sales_group_id: '' });
    if (value) {
      try {
        const sgResponse = await axios.get(
          `${BACKEND_URL}/api/assignments/sales-office-group/by-sales-office/${value}`
        );
        const assignedSGIds = sgResponse.data.map((a) => a.sales_group_id);
        setFilteredSalesGroups(salesGroups.filter((sg) => assignedSGIds.includes(sg.sales_group_id)));
      } catch (error) {
        console.error('Error loading sales group assignments:', error);
      }
    } else {
      setFilteredSalesGroups(salesGroups);
    }
  };

  const handleCancel = () => {
    setShowSaDialog(false);
    setEditingSaIndex(null);
    setSaFormData({ ...EMPTY_SA_FORM });
    setFilteredDistChannels(distChannels);
    setFilteredDivisions(divisions);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto" data-testid="sales-area-dialog">
        <h2 className="text-xl font-semibold mb-4">{editingSaIndex !== null ? 'Edit' : 'Add'} Sales Area</h2>
        <div className="space-y-6">
          {/* Sales Area (Required Together) */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
              Sales Area Assignment (All 3 Required Together)
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Sales Organization *</Label>
                <Select value={saFormData.sales_org_id} onValueChange={onSalesOrgChange}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select Sales Org" /></SelectTrigger>
                  <SelectContent>
                    {salesOrgs.map((so) => (
                      <SelectItem key={so.sales_org_id} value={so.sales_org_id}>
                        {so.sales_org_id} - {so.sales_org_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Distribution Channel *</Label>
                <Select
                  value={saFormData.dist_channel_id}
                  onValueChange={(value) => set({ dist_channel_id: value })}
                  disabled={!saFormData.sales_org_id}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={saFormData.sales_org_id ? 'Select Channel' : 'Select Sales Org first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredDistChannels.map((dc) => (
                      <SelectItem key={dc.dist_channel_id} value={dc.dist_channel_id}>
                        {dc.dist_channel_id} - {dc.dist_channel_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Division *</Label>
                <Select
                  value={saFormData.division_id}
                  onValueChange={(value) => set({ division_id: value })}
                  disabled={!saFormData.sales_org_id}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={saFormData.sales_org_id ? 'Select Division' : 'Select Sales Org first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredDivisions.map((div) => (
                      <SelectItem key={div.division_id} value={div.division_id}>
                        {div.division_id} - {div.division_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-amber-600 mt-2">
              ⚠️ All three fields must be filled together to form a complete Sales Area
            </p>
            {saFormData.sales_org_id && filteredDistChannels.length === 0 && (
              <p className="text-xs text-red-600 mt-1" data-testid="sales-area-no-config">
                No Sales Areas are configured for Sales Organization "{saFormData.sales_org_id}".
                Configure Sales Areas (Sales Org + Distribution Channel + Division) under Sales &amp;
                Distribution master data before assigning one to a customer.
              </p>
            )}
          </div>

          {/* Customer Classification */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Customer Classification</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sales District</Label>
                <Select value={saFormData.sales_district_id} onValueChange={(value) => set({ sales_district_id: value })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select Sales District" /></SelectTrigger>
                  <SelectContent>
                    {salesDistricts.map((sd) => (
                      <SelectItem key={sd.sales_district_id} value={sd.sales_district_id}>
                        {sd.sales_district_id} - {sd.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Customer Group</Label>
                <Select value={saFormData.customer_group_id} onValueChange={(value) => set({ customer_group_id: value })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select Customer Group" /></SelectTrigger>
                  <SelectContent>
                    {customerGroups.map((cg) => (
                      <SelectItem key={cg.customer_group_id} value={cg.customer_group_id}>
                        {cg.customer_group_id} - {cg.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Sales Organization Structure */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Sales Organization Structure</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sales Office</Label>
                <Select value={saFormData.sales_office_id} onValueChange={onSalesOfficeChange}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select Sales Office" /></SelectTrigger>
                  <SelectContent>
                    {salesOffices.map((sof) => (
                      <SelectItem key={sof.sales_office_id} value={sof.sales_office_id}>
                        {sof.sales_office_id} - {sof.sales_office_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sales Group</Label>
                <Select
                  value={saFormData.sales_group_id}
                  onValueChange={(value) => set({ sales_group_id: value })}
                  disabled={!saFormData.sales_office_id}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={saFormData.sales_office_id ? 'Select Sales Group' : 'Select Sales Office first'} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredSalesGroups.map((sg) => (
                      <SelectItem key={sg.sales_group_id} value={sg.sales_group_id}>
                        {sg.sales_group_id} - {sg.sales_group_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Pricing Configuration */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Pricing Configuration</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Currency</Label>
                <Select value={saFormData.currency_code} onValueChange={(value) => set({ currency_code: value })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select Currency" /></SelectTrigger>
                  <SelectContent>
                    {currencies.map((cur) => (
                      <SelectItem key={cur.currency_code} value={cur.currency_code}>
                        {cur.currency_code} - {cur.currency_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Customer Pricing Procedure</Label>
                <Input
                  value={saFormData.customer_pricing_procedure}
                  onChange={(e) => set({ customer_pricing_procedure: e.target.value.toUpperCase() })}
                  placeholder="e.g., ZSTD01"
                  maxLength={10}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Delivery & Shipping */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Delivery & Shipping</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Delivery Priority</Label>
                <Select value={saFormData.delivery_priority_id} onValueChange={(value) => set({ delivery_priority_id: value })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select Priority" /></SelectTrigger>
                  <SelectContent>
                    {deliveryPriorities.map((dp) => (
                      <SelectItem key={dp.delivery_priority_id} value={dp.delivery_priority_id}>
                        {dp.delivery_priority_id} - {dp.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Shipping Conditions</Label>
                <Select value={saFormData.shipping_conditions_id} onValueChange={(value) => set({ shipping_conditions_id: value })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select Shipping" /></SelectTrigger>
                  <SelectContent>
                    {shippingConditions.map((sc) => (
                      <SelectItem key={sc.shipping_conditions_id} value={sc.shipping_conditions_id}>
                        {sc.shipping_conditions_id} - {sc.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Delivering Plant</Label>
                <Select value={saFormData.delivering_plant_id} onValueChange={(value) => set({ delivering_plant_id: value })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select Plant" /></SelectTrigger>
                  <SelectContent>
                    {plants.map((plant) => (
                      <SelectItem key={plant.plant_id} value={plant.plant_id}>
                        {plant.plant_id} - {plant.plant_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Max Partial Deliveries</Label>
                <Input
                  type="number" min="0" max="99"
                  value={saFormData.max_partial_deliveries || ''}
                  onChange={(e) => set({ max_partial_deliveries: e.target.value })}
                  placeholder="0-99"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Incoterms */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Incoterms</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Incoterms</Label>
                <Select value={saFormData.incoterms_id} onValueChange={(value) => set({ incoterms_id: value })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select Incoterms" /></SelectTrigger>
                  <SelectContent>
                    {incoterms.map((inc) => (
                      <SelectItem key={inc.incoterms_id} value={inc.incoterms_id}>
                        {inc.incoterms_id} - {inc.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Incoterms Location</Label>
                <Input
                  value={saFormData.incoterms_location}
                  onChange={(e) => set({ incoterms_location: e.target.value })}
                  placeholder="e.g., New York Port"
                  maxLength={50}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Payment & Control */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Payment & Control</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Terms of Payment (SD)</Label>
                <Select value={saFormData.terms_of_payment_sd} onValueChange={(value) => set({ terms_of_payment_sd: value })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select Payment Terms" /></SelectTrigger>
                  <SelectContent>
                    {termsOfPayment.map((term) => (
                      <SelectItem key={term.payment_key} value={term.payment_key}>
                        {term.payment_key} - {term.sales_text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Account Assignment Group</Label>
                <Input
                  value={saFormData.account_assignment_group}
                  onChange={(e) => set({ account_assignment_group: e.target.value.toUpperCase() })}
                  placeholder="e.g., AAG01"
                  maxLength={10}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Credit Control Area</Label>
                <Select value={saFormData.credit_control_area} onValueChange={(value) => set({ credit_control_area: value })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Select Credit Control Area" /></SelectTrigger>
                  <SelectContent>
                    {creditControlAreas.map((cca) => (
                      <SelectItem key={cca.credit_control_area} value={cca.credit_control_area}>
                        {cca.credit_control_area} - {cca.currency} (Limit: {cca.credit_limit})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Additional Settings */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Additional Settings</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sa_order_combination"
                  checked={saFormData.order_combination}
                  onCheckedChange={(checked) => set({ order_combination: checked })}
                />
                <Label htmlFor="sa_order_combination" className="cursor-pointer">Order Combination</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sa_pod_relevant"
                  checked={saFormData.pod_relevant}
                  onCheckedChange={(checked) => set({ pod_relevant: checked })}
                />
                <Label htmlFor="sa_pod_relevant" className="cursor-pointer">POD Relevant</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sa_rebate"
                  checked={saFormData.rebate}
                  onCheckedChange={(checked) => set({ rebate: checked })}
                />
                <Label htmlFor="sa_rebate" className="cursor-pointer">Rebate</Label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button type="button" variant="outline" onClick={handleCancel} className="flex-1" data-testid="sales-area-cancel-btn">
            Cancel
          </Button>
          <Button type="button" onClick={handleAddSalesArea} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="sales-area-save-btn">
            {editingSaIndex !== null ? 'Update' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SalesAreaDialog;
